import React, { useLayoutEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import type { Address } from 'viem';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useWeb3 } from '@/hooks/useWeb3';
import { useTokenLock } from '@/hooks/useTokenLock';
import { NetworkSwitchDialog } from '@/components/NetworkSwitchDialog';
import { AdminPanel } from '@/components/AdminPanel';
import { TopBar } from '@/components/TopBar';
import { Spinner } from '@/components/Spinner';
import { Icon, ICONS } from '@/components/brand';
import { environment } from '@/config/environment';
import { ConnectScreen } from '@/screens/ConnectScreen';
import { AllocationScreen } from '@/screens/AllocationScreen';
import { GrantsScreen } from '@/screens/GrantsScreen';
import { PayrollScreen } from '@/screens/PayrollScreen';

/**
 * Guard for /position/:lockAddress.
 *
 * The app uses HASH routing, so this fragment is hand-editable and gets pasted
 * between people; a wallet holding two grants will share one of them by URL. An
 * address this wallet does not hold — a typo, somebody else's grant, a link
 * kept after a reassignment — must not render a position, and must not render
 * an error either: it is simply not this wallet's, so it falls back to the
 * wallet's own view.
 *
 * Case is compared insensitively because a checksummed address and a lowercased
 * one are the same grant, and a URL survives being lowercased by a chat client.
 */
const PositionRoute: React.FC<{ lockAddresses: Address[] }> = ({ lockAddresses }) => {
  const { lockAddress } = useParams<{ lockAddress: string }>();
  const held = !!lockAddress && lockAddresses.some(
    (candidate) => candidate.toLowerCase() === lockAddress.toLowerCase(),
  );
  // AllocationScreen reads the same param and renders that one grant.
  return held ? <AllocationScreen /> : <Navigate to="/" replace />;
};

export const App: React.FC = () => {
  const web3 = useWeb3();
  const tokenLock = useTokenLock();
  const { pathname } = useLocation();

  // The factory is Ownable2Step, so a handover leaves a pendingOwner who owns
  // nothing until they accept — and the Accept ownership button lives in the
  // admin panel, so they need to be able to open it. The panel itself spells out
  // that everything else there still belongs to the current owner.
  const canOpenAdmin = tokenLock.isOwner || tokenLock.isPendingOwner;

  // Only a change of top-level section resets scroll. Expanding a lock row also
  // changes the path, and that has to leave the operator exactly where they
  // clicked — scrolling them to the top of a long admin page would be worse than
  // not routing the selection at all.
  const section = pathname.split('/')[1] ?? '';
  const isGrantsRoute = section === 'grants';
  /**
   * Routes that read PUBLIC on-chain configuration and must not be gated behind
   * the allocation checks below.
   *
   * /payroll is here for a reason that is easy to miss: the shell turns away any
   * connected wallet that is neither a beneficiary nor the factory owner, and
   * the monthly payout contract has its OWN owner and its own claimer keys,
   * which need not be either of those. Without this, the key that runs the
   * monthly job — and the treasury wallet, if it holds no vesting grant — would
   * be shown "No allocation found" instead of the screen they came for.
   *
   * It is deliberately NOT added to the two network checks below. /grants only
   * reads, so a wallet on the wrong chain can still use it; /payroll signs, so
   * the network dialog has to keep working there.
   */
  const isPublicRoute = isGrantsRoute || section === 'payroll';
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [section]);

  const formattedAccount = web3.address ? web3.formatAddress(web3.address) : '';

  // States that are true of the whole app rather than of one route: they render
  // in place of the routed area and deliberately leave the URL alone, so a deep
  // link survives a cold load, a wallet reconnect and an RPC failure.
  const renderShell = (): React.ReactNode => {
    // Public on-chain configuration is readable independently of wallet roles.
    if (isPublicRoute) return null;
    if (!web3.isConnected) {
      return <ConnectScreen onConnect={web3.connectWallet} isConnecting={web3.isConnecting} />;
    }

    // rolesLoaded, not just isLoading: isLoading is still false on the first
    // frame after the wallet connects, and isEligible/isOwner/isPendingOwner are
    // all false until the factory read lands. Deciding a route before then would
    // bounce a real owner off /admin and rewrite the URL they pasted.
    //
    // The second clause must stay EXPLICIT about emptiness. It used to read
    // `!tokenLock.lockInfo && !tokenLock.lockAddress`, and those are now arrays:
    // an empty array is TRUTHY in JavaScript, so `!tokenLock.locks` is always
    // false and the whole clause would collapse to a bare `isLoading`. That is
    // not a cosmetic difference — isLoading flips true on every 30-second poll,
    // so the spinner would be thrown back over a fully loaded allocation twice a
    // minute, forever. `.length === 0` is the property actually meant here:
    // show the spinner only while a load is in flight and there is nothing yet.
    if (
      !tokenLock.rolesLoaded ||
      (tokenLock.isLoading &&
        tokenLock.locks.length === 0 &&
        tokenLock.lockAddresses.length === 0)
    ) {
      return (
        <div
          className="sh-main"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)' }}
        >
          <Spinner label="Reading allocation" />
        </div>
      );
    }

    // A failed eligibility read also leaves isEligible false, so it would otherwise
    // fall through to "no allocation found" and tell a real beneficiary they have none.
    if (tokenLock.error && !tokenLock.isEligible && !canOpenAdmin) {
      return (
        <div
          className="sh-main"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)' }}
        >
          <div className="sh-main-inner" style={{ textAlign: 'center', maxWidth: 520 }}>
            <div className="sh-label">Unable to read allocation</div>
            <h1 className="sh-h1" style={{ marginTop: 12 }}>
              We couldn’t reach the{' '}
              <span className="sh-serif" style={{ color: 'var(--accent)' }}>vesting</span> contract.
            </h1>
            <p className="sh-sub" style={{ margin: '14px auto 0' }}>
              This is a network or RPC problem, not a statement about your allocation. Retry in a
              moment — if it persists, check that your wallet is on {environment.chainName}.
            </p>
            <div className="sh-cause" style={{ marginTop: 16 }}>
              {tokenLock.error}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 28 }}>
              {/* A reload keeps the fragment, so this retries the same route. */}
              <button type="button" onClick={() => window.location.reload()} className="sh-btn sh-btn-primary">
                Retry
                <Icon d={ICONS.arrowRight} size={14} />
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (!tokenLock.isEligible && !canOpenAdmin) {
      return (
        <div
          className="sh-main"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)' }}
        >
          <div className="sh-main-inner" style={{ textAlign: 'center', maxWidth: 520 }}>
            <div className="sh-label">No allocation found</div>
            <h1 className="sh-h1" style={{ marginTop: 12 }}>
              No{' '}
              <span className="sh-serif" style={{ color: 'var(--accent)' }}>SAHARA</span> allocation
              is assigned to this wallet.
            </h1>
            <p className="sh-sub" style={{ margin: '14px auto 0' }}>
              Wallet{' '}
              <span className="sh-mono" style={{ color: 'var(--text)' }}>{formattedAccount}</span>{' '}
              is not registered for an allocation in the vesting contract. Try a different wallet, or
              contact Sahara AI support if you believe your allocation should be assigned here.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 28 }}>
              <button
                type="button"
                onClick={web3.disconnectWallet}
                className="sh-btn"
              >
                <Icon d={ICONS.power} size={14} />
                Disconnect
              </button>
              <button
                type="button"
                onClick={web3.openModal}
                className="sh-btn sh-btn-primary"
              >
                Switch account
                <Icon d={ICONS.arrowRight} size={14} />
              </button>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const shell = renderShell();

  // Hiding the Admin tab is presentation; this is the enforcement. Hash URLs are
  // hand-editable, so the route has to turn a non-owner away on its own.
  const adminElement = canOpenAdmin ? (
    <div className="sh-main">
      <div className="sh-main-inner">
        <div style={{ marginBottom: 32 }}>
          <div className="sh-label">Operations</div>
          <h1 className="sh-h1">
            Admin{' '}
            <span className="sh-serif" style={{ color: 'var(--text-2)' }}>panel</span>
          </h1>
          <p className="sh-sub" style={{ marginTop: 14 }}>
            Manage the vesting factory, fund and pause locks, and rotate beneficiaries. Every
            action is on-chain and requires a wallet signature.
          </p>
        </div>
        <AdminPanel />
      </div>
    </div>
  ) : (
    <Navigate to="/" replace />
  );

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3500}
        theme="dark"
        hideProgressBar={false}
        newestOnTop
        pauseOnFocusLoss={false}
        pauseOnHover
        draggable={false}
      />
      <NetworkSwitchDialog
        isOpen={!isGrantsRoute && web3.showNetworkDialog}
        onSwitchNetwork={web3.switchToCorrectNetwork}
        onClose={web3.closeNetworkDialog}
      />

      <div style={{ minHeight: '100vh', position: 'relative', background: 'var(--bg)' }}>
        <TopBar
          isConnected={web3.isConnected ?? false}
          address={web3.address ?? undefined}
          formatAddress={web3.formatAddress}
          chainName={environment.chainName}
          isCorrectNetwork={isGrantsRoute || web3.isCorrectNetwork}
          isOwner={canOpenAdmin}
          showPayouts={canOpenAdmin && environment.distributorAddress !== null}
          showAllocation={tokenLock.isEligible || !canOpenAdmin}
          onOpenWallet={web3.openModal}
        />

        <main>
          {shell ?? (
            <Routes>
              <Route path="/grants" element={<GrantsScreen />} />
              <Route path="/grants/lock/:address" element={<GrantsScreen />} />
              <Route path="/grants/*" element={<Navigate to="/grants" replace />} />
              {/* Both payroll paths render the SAME element at the SAME depth, so
                  React reconciles rather than remounts: expanding a payment keeps
                  the loaded roster and any half-filled form intact, exactly as
                  /admin and /admin/lock do. The screen turns a wallet with no
                  role there into a read-only view on its own — the roster is
                  public on-chain data, so there is nothing to hide behind a
                  redirect, and a redirect would lock out the claimer key. */}
              <Route path="/payroll" element={<PayrollScreen />} />
              <Route path="/payroll/entry/:id" element={<PayrollScreen />} />
              <Route path="/payroll/*" element={<Navigate to="/payroll" replace />} />
              {/* An owner with no allocation of their own has nothing to see here.
                  Declarative and replace-based, so it cannot push history, cannot
                  double-fire under StrictMode, and cannot reach into /admin/lock. */}
              <Route
                path="/"
                element={
                  canOpenAdmin && !tokenLock.isEligible ? (
                    <Navigate to="/admin" replace />
                  ) : (
                    <AllocationScreen />
                  )
                }
              />
              {/* One wallet may now hold several grants, so a position needs its
                  own address in the URL to be linkable at all.

                  "/" deliberately RENDERS the sole position rather than
                  redirecting to it: a redirect would rewrite the URL of anyone
                  with exactly one grant, which is almost everybody, and it would
                  do so asynchronously after the address read lands — so the
                  bookmark they made yesterday and the link they pasted would
                  both end up somewhere they did not type. AllocationScreen reads
                  useParams().lockAddress: absent with exactly one grant it shows
                  that grant, absent with several it shows the list, and present
                  it shows the named one. Both routes render the SAME element at
                  the SAME depth, so React reconciles instead of remounting and
                  picking a grant keeps whatever was already loaded. */}
              <Route
                path="/position/:lockAddress"
                element={<PositionRoute lockAddresses={tokenLock.lockAddresses} />}
              />
              {/* Both admin paths render the same element at the same depth, so
                  React reconciles rather than remounts: selecting a lock keeps the
                  panel's loaded details and half-filled forms intact. */}
              <Route path="/admin" element={adminElement} />
              <Route path="/admin/lock/:address" element={adminElement} />
              <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          )}
        </main>

        <footer
          style={{
            borderTop: '1px solid var(--border)',
            padding: '24px 56px',
            color: 'var(--text-muted)',
            fontSize: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div className="sh-mono" style={{ letterSpacing: '0.04em' }}>
            © {new Date().getFullYear()} Sahara Labs · Token vesting
          </div>
          <div style={{ display: 'flex', gap: 18 }}>
            <a href="https://saharaai.com" target="_blank" rel="noreferrer" style={{ color: 'var(--text-2)' }}>
              saharaai.com
            </a>
            <span className="sh-mono">v1.0</span>
          </div>
        </footer>
      </div>
    </>
  );
};
